// =========================================================
// The landing page, in reading order:
//
//   hero + trust bar → قبل الريّس → يوم الريّس → إزاي تبدأ → السعر → جوّه الريّس
//   → لمين → إيه اللي بيتغير → أسئلة → الموديولز → آراء العملاء (only when real)
//   → كلّمنا → footer
//
// Same CMS loading, loading shell, PageMeta and SEO logic as Pages/Home/home.js.
//
// IMAGE CHOICES. Each candidate was looked at, at size, under its overlay.
//
//   Hero slides       rooftop, install, stock, money: all four, the brand line first
//                     on rooftop because it is the strongest shot.
//   Problems feature  rooftop at object-position 50% 62%. A 2.3:1 slot; it holds
//                     up that wide, and the crop keeps the emblem on the upper unit
//                     out of frame. install at the same crop is a dark wall.
//   Pillars           jobs install, techs rooftop, stock stock, money money, as specified.
//   Pillars «بالليل»  public/pricing-bg.jpg, the graphite silk, under the chapter
//                     overlay. It reads as night, sits exactly in our graphite, and
//                     has no text. Rejected:
//                       hero-bg.png: invented figures ($120,000, 8,200 customers), in
//                         English and dollars, and a posed man. BRAND.md §7 forbids it.
//                       work-cycle-bg.jpg: pale lavender streaks, and it turns a flat
//                         blue-grey under the overlay.
//                       a darkened product-board capture: murky, and the greeting
//                         still reads through it.
//                     pricing-bg is 6.1 MB, so media.js serves img/night-*.webp: the
//                     same photo at web sizes (39 KB at 1920), with less colour so it
//                     holds graphite.
//   Pricing thumbs    board (free month), jobs (monthly), finance (yearly).
//   جوّه الريّس        row 1 board, jobs, stock; row 2 finance, invoices.
//                     NEVER product-customers.png: it shows real-looking phone numbers.
//   Sectors bento     large install; small rooftop (zoomed so the emblem is out),
//                     stock, install zoomed on the ladder, money; wide board.
//                     Crops are in copy.js SECTORS.
//   FAQ band          stock. Under the 88 to 95% overlay the shelving reads as faint
//                     structure lines, like the reference's pipes; rooftop reads as a
//                     big dark shape behind the questions.
//   Module cards      jobs, stock, invoices, each its chapter's own screen.
//   Contact band      rooftop. Under the 84 to 94% overlay the grille stays visible
//                     and calm. install goes black there, and stock competes with the
//                     glass panel.
//
// The captures are the same files AuthPage already shows publicly, resized in
// img/ (media.js says why).
// =========================================================

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PageMeta from "../../Components/PageMeta";
import { fetchPublicHomeContent, EMPTY_HOME_CONTENT } from "../../services/homeContentApi";
import { PreviewNotice, useLandingPreview } from "./preview";
import "./landing-base.css";
import { buildLandingData } from "./data";
import { useMotionAllowed } from "./motion/env";
import { scrollToElement, useSmoothScroll } from "./motion/smoothScroll";
import useReveal from "./motion/useReveal";
import LandingHeader from "./LandingHeader";
import LandingFooter from "./LandingFooter";
import { fitTrial } from "../Home/content";
import LandingCursor from "./LandingCursor";
import Hero from "./Hero";
import Problems from "./Problems";
import Pillars from "./Pillars";
import Method from "./Method";
import PricingBand from "./PricingBand";
import Marquee from "./Marquee";
import Sectors from "./Sectors";
import Outcomes from "./Outcomes";
import FaqBand from "./FaqBand";
import Insights from "./Insights";
import ContactBand from "./ContactBand";

// index.html loads Alexandria at 700 and 800 for the rest of the product. This
// page's display type is 300 and 600, so a light H1 would otherwise render bold.
const FONT_LINK_ID = "ld-display-font";
const FONT_HREF = "https://fonts.googleapis.com/css2?family=Alexandria:wght@300;600&display=swap";

const CMS_PATIENCE_MS = 4000;

// While the page is mounted: rem is 1vw at 1024 and up, and the display weights
// load. Before the first paint, and undone on the way out, so the app never sees it.
function useLandingRoot() {
    useLayoutEffect(() => {
        const html = document.documentElement;
        html.classList.add("ld-root");
        html.lang = "ar";
        html.dir = "rtl";
        if (!document.getElementById(FONT_LINK_ID)) {
            const link = document.createElement("link");
            link.id = FONT_LINK_ID;
            link.rel = "stylesheet";
            link.href = FONT_HREF;
            document.head.appendChild(link);
        }
        return () => html.classList.remove("ld-root");
    }, []);
}

const Landing = () => {
    const rootRef = useRef(null);
    const [content, setContent] = useState(null);
    const motion = useMotionAllowed();
    // Only with a ?preview= token. See preview.js.
    const preview = useLandingPreview({
        onContent: setContent,
        onScroll: (where) => {
            const target = document.querySelector(where);
            if (target) scrollToElement(target);
        },
    });
    const { token: previewToken, load: loadPreview, announce: announcePreview } = preview;

    useLandingRoot();
    useSmoothScroll(rootRef);

    // Fetch the live CMS content on mount. On failure, or on an answer with no
    // document in it, we render with EMPTY_HOME_CONTENT and data.js fills every
    // section from content.js. An API that has not answered after CMS_PATIENCE_MS
    // gets the same treatment, so the hero is never left empty; if the document
    // arrives after that it still replaces the fallbacks.
    useEffect(() => {
        let cancelled = false;
        const patience = setTimeout(() => {
            setContent((current) => current || EMPTY_HOME_CONTENT);
        }, CMS_PATIENCE_MS);
        (async () => {
            try {
                const next = previewToken ? await loadPreview() : await fetchPublicHomeContent();
                if (!cancelled) {
                    setContent(next || EMPTY_HOME_CONTENT);
                    announcePreview();
                }
            } catch {
                if (!cancelled) setContent((current) => current || EMPTY_HOME_CONTENT);
            }
            clearTimeout(patience);
        })();
        return () => {
            cancelled = true;
            clearTimeout(patience);
        };
    }, [previewToken, loadPreview, announcePreview]);

    useReveal(rootRef, content);

    // A link with a hash can arrive before its section exists; the sections
    // mount with the content, so the jump happens then, and only once: a document
    // arriving late must not pull a visitor who has already started reading.
    const jumped = useRef(false);
    useEffect(() => {
        if (!content || jumped.current || !window.location.hash) return;
        const target = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
        if (target) {
            jumped.current = true;
            scrollToElement(target);
        }
    }, [content]);

    const data = useMemo(() => buildLandingData(content), [content]);

    // Defaults match Back-End/utils/seo.js exactly. The server writes the head
    // for the crawler's first pass and react-helmet rewrites it after hydration;
    // they have to be the same sentence (see Pages/Home/home.js).
    const cmsTitle = content?.seo?.metaTitle || "الريّس — نظام تشغيل شركات التكييف في مصر";
    const cmsDescription = fitTrial(
        content?.seo?.metaDescription ||
        "نظام واحد لإدارة شركة التكييف: المشاوير، الأسطوات، العهدة، الخزنة، والفواتير — بالعربي وبتقارير لحظية. جرّب شهرًا مجانًا.",
        content?.pricing?.trialDays,
    );

    return (
        <div className={`ld-page ${motion ? "ld-motion" : ""}`} ref={rootRef}>
            <a href="#ld-main" className="ld-skip">تخطّي للمحتوى</a>
            <LandingCursor />
            {content ? (
                <PageMeta
                    title={cmsTitle}
                    description={cmsDescription}
                    // Same sources the server's head uses (Back-End/utils/seo.js).
                    {...(content.seo?.ogImage ? { ogImage: content.seo.ogImage } : {})}
                    {...(content.seo?.canonicalUrl ? { canonical: content.seo.canonicalUrl } : {})}
                    noIndex={!!previewToken}
                />
            ) : (
                <PageMeta
                    title="الصفحة الرئيسية"
                    description="الريّس — منصة متكاملة لإدارة شركات التكييف والتبريد في مصر: مخزون، فواتير، محاسبة، تجار، تقارير لحظية."
                    noIndex={!!previewToken}
                />
            )}
            <LandingHeader
                nav={data.nav}
                phone={data.phone}
                ctaText={data.hero.ctaText}
                ctaHref={data.hero.ctaHref}
            />
            {content ? (
                <>
                    <main id="ld-main">
                        <Hero hero={data.hero} secondary={data.secondary} trust={data.trust} />
                        <Problems story={data.story} />
                        <Pillars day={data.day} planNote={data.planNote} cta={data.cta} />
                        <Method how={data.how} vars={data.vars} ctaHref={data.cta.href} />
                        <PricingBand pricing={data.pricing} cards={data.priceCards} finePrint={data.finePrint} aside={data.priceAside} />
                        <Marquee />
                        <Sectors who={data.who} />
                        <Outcomes outcomes={data.outcomes} />
                        <FaqBand faq={data.faq} vars={data.vars} ctaText={data.hero.ctaText} ctaHref={data.hero.ctaHref} />
                        <Insights day={data.day} testimonials={data.testimonials} hasTestimonials={data.hasTestimonials} />
                        <ContactBand contact={data.contact} />
                    </main>
                    <LandingFooter
                        cta={data.cta}
                        tagline={`${data.hero.title} ${data.hero.titleAccent}`}
                        email={data.email}
                        phone={data.phone}
                        social={data.social}
                        hasPricing={data.hasPricing}
                        hasTestimonials={data.hasTestimonials}
                        legal={data.legal}
                    />
                    <PreviewNotice preview={preview} />
                </>
            ) : (
                <main id="ld-main">
                    <section className="ld-shell" id="hero" aria-busy="true" />
                </main>
            )}
        </div>
    );
};

export default Landing;
